// Shared understanding of how a room allocation sheet is laid out.
// The parser and the format detector both read these rules so they cannot drift apart.

export const COLUMN_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];

export const COLUMN_INDEX = COLUMN_LETTERS.reduce((map, letter, index) => {
  map[letter] = index;
  return map;
}, {});

const cellText = (row, index) => String((row && row[index]) || "").trim();

/**
 * Compare header text ignoring case, full stops, surrounding blanks and repeated spaces.
 * Used for the strict "does the file match Settings" check.
 */
export const normalizeHeader = (value) =>
  String(value || "")
    .replace(/\./g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

/**
 * A looser form used to recognise a header by meaning rather than by exact text,
 * so "Day/Time", "Day - Time" and "day time" all reduce to the same token.
 */
export const headerToken = (value) =>
  String(value || "")
    .replace(/[^a-z0-9]+/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// Wording seen across the sheets this app is used with, per column
export const COLUMN_SYNONYMS = {
  A: ["item no", "item number", "item", "sl no", "s n", "no"],
  B: ["unit no", "unit number", "unit code", "short code", "code"],
  C: ["vle class name", "class name", "vle name", "vle", "class"],
  D: ["unit name", "module name", "unit", "module", "subject"],
  E: ["lecturer", "tutor name", "tutor", "teacher", "staff", "staff name"],
  F: ["day time", "day", "day of week", "daytime"],
  G: ["ew time", "ew", "evening weekend", "ew day time"],
  H: ["group", "grp", "group name"],
  I: ["cohort", "intake", "cohort date"],
  J: ["room allocated", "room allocation", "room", "allocated room", "rooms"],
  K: ["students coming", "students", "no of students", "student count", "expected students"],
  L: ["student number", "student numbers", "student no", "student nos"],
};

const ALL_SYNONYMS = new Set(
  Object.values(COLUMN_SYNONYMS).reduce((all, list) => all.concat(list), [])
);

// A section title normally names a course, a level, a semester or an intake year
export const SECTION_KEYWORDS = [
  "MSc",
  "MBA",
  "Sem",
  "Foundation",
  "Year",
  "Undergraduate",
  "Postgraduate",
  "Hons",
  "Level",
  "Diploma",
  "Certificate",
  "HND",
  "HNC",
  "BMS",
  "Cohort",
];

/** Does this text read like a course/cohort heading? */
export const matchesSectionKeywords = (title) => {
  const text = String(title || "");
  if (!text.trim()) return false;
  if (SECTION_KEYWORDS.some((word) => text.toLowerCase().includes(word.toLowerCase()))) {
    return true;
  }
  return Boolean(text.match(/\d{4}/) && text.length > 10);
};

/**
 * A row carrying a title and no data of its own. This catches headings the keyword
 * list does not know, such as "Health and Social Care Courses", without swallowing
 * a data row, which always fills at least one of D, E, F or K.
 */
const isStandaloneTitle = (row) => {
  const title = cellText(row, COLUMN_INDEX.A);
  if (title.length < 3) return false; // item numbers and short codes
  if (/^[\d\s.,\-/]+$/.test(title)) return false; // purely numeric, e.g. "28"
  return COLUMN_LETTERS.slice(3).every((letter) => cellText(row, COLUMN_INDEX[letter]) === "");
};

/**
 * The single rule deciding whether a row is rendered as a full-width section title.
 * Column A carries the title, B and C are empty, and the row either reads like a
 * course heading or carries no data at all.
 */
export const isSectionRow = (row) => {
  if (!row || row.length === 0) return false;
  const title = cellText(row, COLUMN_INDEX.A);
  if (!title) return false;
  if (cellText(row, COLUMN_INDEX.B) !== "") return false;
  if (cellText(row, COLUMN_INDEX.C) !== "") return false;
  return matchesSectionKeywords(title) || isStandaloneTitle(row);
};

/**
 * Find the row holding the column names.
 * Scores each of the first few rows on how many cells read like a known header,
 * preferring the wording already configured in Settings.
 * @returns {{rowNumber: number, score: number, names: object, filled: number}|null}
 */
export const detectHeaderRow = (rawData, configuredHeaders = {}, maxScan = 10) => {
  if (!Array.isArray(rawData)) return null;

  let best = null;
  const limit = Math.min(rawData.length, maxScan);

  for (let i = 0; i < limit; i++) {
    const row = rawData[i] || [];
    const names = {};
    let score = 0;
    let filled = 0;

    COLUMN_LETTERS.forEach((letter) => {
      const text = cellText(row, COLUMN_INDEX[letter]);
      names[letter] = text;
      if (!text) return;

      filled++;
      const token = headerToken(text);
      const configured = headerToken(configuredHeaders[letter]);

      if (configured && token === configured) {
        score += 3; // exactly what Settings expects, in the right column
      } else if ((COLUMN_SYNONYMS[letter] || []).includes(token)) {
        score += 2; // known wording for this column
      } else if (ALL_SYNONYMS.has(token)) {
        score += 1; // header vocabulary, but in an unexpected column
      }
    });

    // A header row is dense and scores across several columns
    if (filled >= 4 && score >= 6 && (!best || score > best.score)) {
      best = { rowNumber: i + 1, score, names, filled };
    }
  }

  return best;
};
